import React from 'react';
import { Route, Routes } from 'react-router-dom';
import AppRoutes from './AppRoutes';
import { Modal, Layout, Typography, Flex, Button, theme } from 'antd';
import { BananaIcon } from './components/shared/ApeIcons';
import { LeftMenu } from './components/layout/LeftMenu'
import { useWeb3ModalProvider, useWeb3ModalAccount } from '@web3modal/ethers/react'
import { TopMenu } from './components/layout/TopMenu'
import { InfoBar } from './components/layout/InfoBar'
import { BrowserProvider } from 'ethers'
import { SocketsProvider } from './components/context/SocketsContext';
import { PermissionsProvider } from './components/context/PermissionsContext';
import { AnimationProvider } from './components/context/AnimationContext';
import { GridProvider } from './components/context/GridContext';
import { ThemeProvider } from './components/context/ThemeContext';
import { ToastProvider } from './components/shared/Toaster';
import { YouTubeEmbed } from './components/shared/YouTubeEmbed';
import { MiniKitProvider } from '@coinbase/onchainkit/minikit';
import { sdk } from '@farcaster/miniapp-sdk'
import { BottomNav } from './components/layout/BottomNav'
import { RoughEdgeFilters } from './components/shared/RoughEdgeFilters';
import { useSEO } from './hooks/useSEO';
import { useChainAccent } from './hooks/useChainAccent';
import { PageErrorBoundary } from './components/shared/ErrorBoundary';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './components/shared/custom.css';

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 30_000,
			gcTime: 5 * 60_000,
			refetchOnWindowFocus: false,
			retry: (failureCount, error) => {
				if (error?.response?.status === 404 || error?.response?.status === 403) return false;
				return failureCount < 2;
			},
		},
	},
});

const { Sider } = Layout;
const { Text } = Typography;
const { Content } = Layout;

export const useSessionState = (defaultValue, key) => {
	const [value, setValue] = React.useState(() => {
		const stickyValue = window.sessionStorage.getItem(key);
		return stickyValue !== null ? JSON.parse(stickyValue) : defaultValue;
	});
	React.useEffect(() => {
		window.sessionStorage.setItem(key, JSON.stringify(value));
	}, [key, value]);
	return [value, setValue];
}

export const useLocalState = (defaultValue, key) => {
	const [value, setValue] = React.useState(() => {
		const stickyValue = window.localStorage.getItem(key);
		try {
			return stickyValue !== null ? JSON.parse(stickyValue) : defaultValue;
		}
		catch {
			return defaultValue;
		}
	});
	React.useEffect(() => {
		window.localStorage.setItem(key, JSON.stringify(value));
	}, [key, value]);
	return [value, setValue];
}

export const App = () => {
	const { walletProvider } = useWeb3ModalProvider();
	const { chainId } = useWeb3ModalAccount();
	useSEO();
	useChainAccent();

	const [mobile, setMobile] = React.useState(false);
	const [isModalOpen, setIsModalOpen] = useSessionState(true, "instructions.open");
	const [menuCollapsed, setMenuCollapsed] = React.useState(true);
	const [sidebarHidden, setSidebarHidden] = React.useState(false);
	const [isAtBottom, setIsAtBottom] = React.useState(false);

	const {
		token: { bgContent, bgLeftMenu },
	} = theme.useToken();

	const leftMenuWidth = mobile ? 0 : (sidebarHidden ? 0 : 225);

	const handleChainChange = async (chain) => {
		if (chain != 0) {
			try {
				const provider = new BrowserProvider(walletProvider);
				const chainHex = '0x' + (chain).toString(16);
				await provider.send('wallet_switchEthereumChain', [{ chainId: chainHex }]);
			} catch (err) {
				console.warn('Chain switch failed:', err.message || err);
			}
		}
	}

	React.useEffect(() => {
		const handleScroll = () => {
			const { innerHeight } = window;
			const { scrollHeight, scrollTop } = document.documentElement;

			if (innerHeight + scrollTop >= scrollHeight - 10) {
				setIsAtBottom(true);
			} else {
				setIsAtBottom(false);
			}
		};

		window.addEventListener('scroll', handleScroll);

		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, []);

	React.useEffect(() => {
		sdk.actions.ready();
	}, []);


	return (
		<QueryClientProvider client={queryClient}>
		<ThemeProvider>
		<ToastProvider>
		<MiniKitProvider projectId="4f7478fb-e859-49cb-973d-743d5910af78">
			<SocketsProvider mobile={mobile}>
				<PermissionsProvider>
					<AnimationProvider>
						<GridProvider isAtBottom={isAtBottom}>
							<Layout style={{ width: "100%", height: "100%", minHeight: '100vh' }}>
								<RoughEdgeFilters />
								<Layout>
									{mobile && !menuCollapsed && (
										<div onClick={() => setMenuCollapsed(true)}
											style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.5)' }} />
									)}
									<Sider onBreakpoint={(broken) => { setMobile(broken) }} collapsed={mobile ? menuCollapsed : sidebarHidden} breakpoint="md" collapsedWidth={0} trigger={null} width={mobile ? 250 : 225}
									style={{
										backgroundColor: 'var(--color-night)', position: 'fixed', height: "100%", zIndex: 101,
										borderRight: '3px solid #000',
										boxShadow: mobile && !menuCollapsed ? '8px 0 30px rgba(0,0,0,0.45)' : 'none',
										transition: 'width 280ms cubic-bezier(.4,0,.2,1), margin-left 280ms cubic-bezier(.4,0,.2,1)',
										marginLeft: (!mobile && sidebarHidden) ? '-225px' : '0px',
										overflow: 'hidden'
									}}>
										<LeftMenu mobile={mobile} menuCollapsed={menuCollapsed} setMenuCollapsed={setMenuCollapsed} handleChainChange={handleChainChange} chainId={chainId} />
									</Sider>
									<Layout style={{ marginLeft: `${leftMenuWidth}px`, transition: 'margin-left 280ms cubic-bezier(.4,0,.2,1)' }}>
										{!mobile && <div style={{ position: 'relative' }}>
											<InfoBar />
											<button
												onClick={() => setSidebarHidden(!sidebarHidden)}
												title="Toggle sidebar"
												style={{
													position: 'absolute', top: '7px', left: '14px', zIndex: 8,
													display: 'flex', height: '32px', width: '32px', cursor: 'pointer',
													alignItems: 'center', justifyContent: 'center',
													borderRadius: '9px', border: '2px solid var(--bd)',
													background: 'var(--card)',
													boxShadow: '2px 2px 0 rgba(0,0,0,.2)',
													transition: 'transform 140ms cubic-bezier(.34,1.56,.64,1), background 140ms, box-shadow 140ms',
												}}
												onMouseEnter={(e) => { e.currentTarget.style.transform = 'rotate(-8deg) scale(1.12)'; e.currentTarget.style.background = 'var(--accent)'; e.currentTarget.style.boxShadow = '1px 2px 0 rgba(38,37,31,.25)'; }}
												onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.background = 'var(--card)'; e.currentTarget.style.boxShadow = '2px 2px 0 rgba(0,0,0,.2)'; }}
											>
												<svg width={15} height={12} viewBox="0 0 15 12" fill="none">
													<path d="M1 1h13M1 6h13M1 11h13" stroke="var(--ink)" strokeWidth="2" strokeLinecap="round" />
												</svg>
											</button>
										</div>}
										{mobile && <TopMenu mobile={mobile} setMenuCollapsed={setMenuCollapsed} handleChainChange={handleChainChange} />}
										<Content
											style={{
												padding: 0,
												margin: 0,
												backgroundColor: 'var(--bg)'
											}}
										>
											<PageErrorBoundary name="router">
											<Routes>
												{AppRoutes.map((route, index) => {
													const { element, ...rest } = route;
													return <Route key={index} {...rest} element={element} />;
												})}
											</Routes>
										</PageErrorBoundary>
										</Content>
									</Layout>
								</Layout>
							</Layout>
							<BottomNav />
						</GridProvider>
					</AnimationProvider>
				</PermissionsProvider>
			</SocketsProvider>
		</MiniKitProvider>
		</ToastProvider>
		</ThemeProvider>
		</QueryClientProvider>
	);
}
